import type { CSSProperties } from 'react'
import { formatTime, isAllDay } from '../../lib/calendar'
import { accountSwatch, calloutsFor, externalContacts, isThreat, kindOf, type Callout } from '../../lib/meetingCallouts'
import type { MeetingView } from '../../types/calendar'

// One meeting, drawn three ways: a chip on the month grid, a block on the week, a row on the agenda.
// The same language on each: the swatch is the account's, the kind sets the weight (client emphasised,
// colleagues dimmed, an unknown outside room flagged), and the callout markers say what Scout knows.

export const swatchStyle = (m: MeetingView): CSSProperties => ({ ['--swatch' as string]: m.meeting.companyId ? `var(--cal-account-${accountSwatch(m.meeting.companyId)})` : 'var(--cal-none)' })

export function chipClass(m: MeetingView, selected: boolean, extra = ''): string {
  // a rival in the room is red on every view, whatever colour the account carries
  return ['cal-item', `kind-${kindOf(m)}`, isThreat(m) ? 'threat' : '', selected ? 'selected' : '', m.meeting.isCancelled ? 'cancelled' : '', extra].filter(Boolean).join(' ')
}

/** The marker dots on a chip or block: one per callout, titled in full. */
export function Markers({ callouts, max = 4 }: { callouts: Callout[]; max?: number }) {
  if (callouts.length === 0) return null
  return (
    <span className="cal-markers" aria-label={callouts.map((c) => c.label).join(', ')}>
      {callouts.slice(0, max).map((c) => <i key={c.key} className={`cal-marker tone-${c.tone}`} title={c.label}>{c.short}</i>)}
      {callouts.length > max && <i className="cal-marker tone-quiet" title={callouts.slice(max).map((c) => c.label).join(', ')}>+{callouts.length - max}</i>}
    </span>
  )
}

export function CalloutList({ callouts, onPrep }: { callouts: Callout[]; onPrep?: () => void }) {
  if (callouts.length === 0) return null
  return (
    <ul className="cal-callouts">
      {callouts.map((c) => (
        <li key={c.key} className={`cal-callout tone-${c.tone}`}>
          {c.key === 'prep' && onPrep ? <button className="cal-callout-action" onClick={(e) => { e.stopPropagation(); onPrep() }}>{c.label}</button> : c.label}
        </li>
      ))}
    </ul>
  )
}

export function MonthChip({ m, now, selected, onPick }: { m: MeetingView; now: Date; selected: boolean; onPick: (m: MeetingView) => void }) {
  const callouts = calloutsFor(m, now)
  const who = m.companyName ?? externalContacts(m)[0]?.name ?? null
  return (
    <button className={chipClass(m, selected, 'cal-chip')} style={swatchStyle(m)} onClick={() => onPick(m)} title={`${m.meeting.subject}${who ? ` · ${who}` : ''}`}>
      <span className="cal-chip-time">{isAllDay(m.meeting.start, m.meeting.end) ? 'All day' : formatTime(m.meeting.start)}</span>
      <span className="cal-chip-text">{m.meeting.subject || '(no subject)'}</span>
      <Markers callouts={callouts} max={3} />
    </button>
  )
}

export function WeekBlock({ m, now, selected, onPick, style }: { m: MeetingView; now: Date; selected: boolean; onPick: (m: MeetingView) => void; style: CSSProperties }) {
  const callouts = calloutsFor(m, now)
  const who = m.companyName ?? externalContacts(m)[0]?.name ?? null
  const height = typeof style.height === 'number' ? style.height : 60
  const short = height < 40      // a half-hour block: one line, time and title together
  const tall = height >= 52      // room for the account under the title
  return (
    <button className={chipClass(m, selected, `cal-block${short ? ' short' : ''}`)} style={{ ...style, ...swatchStyle(m) }} onClick={() => onPick(m)} title={`${formatTime(m.meeting.start)} – ${formatTime(m.meeting.end)} · ${m.meeting.subject}${who ? ` · ${who}` : ''}`}>
      {short ? (
        <span className="cal-block-line">
          <span className="cal-block-time">{formatTime(m.meeting.start)}</span>
          <span className="cal-block-text">{m.meeting.subject || '(no subject)'}</span>
          <Markers callouts={callouts} max={2} />
        </span>
      ) : (
        <>
          <span className="cal-block-head">
            <span className="cal-block-time">{formatTime(m.meeting.start)}</span>
            <Markers callouts={callouts} max={3} />
          </span>
          <span className="cal-block-text">{m.meeting.subject || '(no subject)'}</span>
          {tall && who && <span className="cal-block-who">{who}</span>}
        </>
      )}
    </button>
  )
}

export function AllDayChip({ m, now, selected, onPick }: { m: MeetingView; now: Date; selected: boolean; onPick: (m: MeetingView) => void }) {
  return (
    <button className={chipClass(m, selected, 'cal-chip all-day')} style={swatchStyle(m)} onClick={() => onPick(m)} title={m.meeting.subject}>
      <span className="cal-chip-text">{m.meeting.subject || '(no subject)'}</span>
      <Markers callouts={calloutsFor(m, now)} max={2} />
    </button>
  )
}
